import { Suspense, useState } from 'react';
import { Await, useLoaderData, useNavigate } from 'react-router-dom';
import { PreviousPageButton } from '@/components/BackButton';
import { IntlIcon } from '@/components/IntlIcon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { SectionHeading } from '@/components/ui/section-heading';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AdditionalVehicleSheet } from '@/components/vin/AdditionalVehicleSheet';
import { AssociatedContractsTable } from '@/components/vin/AssociatedContractsTable';
import { VehicleFacts } from '@/components/vin/VehicleFacts';
import { VehicleIdentityRow } from '@/components/vin/VehicleIdentityRow';
import { toSafeExternalUrl } from '@/lib/externalUrl';
import type { VinLoaded, VinLoaderData } from '@/routes/loaders';

function ErrorCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-white px-4 py-6 sm:px-6 lg:px-8">
      <div className="intl-card border-red-200 px-6 py-8 text-center">
        {children}
      </div>
    </div>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: 'warning' | 'error';
  children: React.ReactNode;
}) {
  const classes =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-red-200 bg-red-50 text-red-700';

  return (
    <div className={`intl-card mb-4 px-6 py-4 text-[14px] ${classes}`}>
      {children}
    </div>
  );
}

function LoadedVin({ vin, loaded }: { vin: string; loaded: VinLoaded }) {
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const { detail: detailResult, contracts: contractsResult } = loaded;
  const detail = detailResult.ok ? detailResult.value : null;
  const contracts = contractsResult.ok ? contractsResult.value : [];
  const healthReportUrl = detail?.vehicleHealthReportUri
    ? toSafeExternalUrl(detail.vehicleHealthReportUri)
    : null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-full bg-white">
        <PreviousPageButton />

        <div className="px-6 pb-6 first:pt-6">
          {detailResult.ok ? null : (
            <Notice tone="warning">{detailResult.message}</Notice>
          )}
          {contractsResult.ok ? null : (
            <Notice tone="error">{contractsResult.message}</Notice>
          )}

          <Panel variant="data">
            <div className="flex flex-col gap-1 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2">
                <h1 className="text-[20px] font-medium leading-5 text-black">
                  Vehicle - {detail?.vin ?? vin}
                </h1>
                <VehicleIdentityRow detail={detail} />
              </div>

              <Button
                type="button"
                variant="outline"
                disabled={!healthReportUrl}
                onClick={() => {
                  if (healthReportUrl) {
                    window.open(healthReportUrl, '_blank', 'noopener');
                  }
                }}
                className="h-8 shrink-0 gap-2 rounded-md border-[var(--intl-primary-surface)] bg-transparent px-4 py-2 text-[16px] font-medium uppercase leading-[18px] text-black hover:bg-[var(--intl-primary-surface)]/5"
              >
                Health report <IntlIcon name="launch" className="h-4 w-4" />
              </Button>
            </div>
          </Panel>

          <div className="mt-2 space-y-2">
            <VehicleFacts
              detail={detail}
              vin={vin}
              onMoreDetails={() => setMoreDetailsOpen(true)}
            />

            <div className="rounded-md px-4">
              <div className="flex items-center gap-2">
                <SectionHeading>Associated Contracts</SectionHeading>
                <Badge variant="count">{contracts.length}</Badge>
              </div>

              {contracts.length === 0 ? (
                <div className="pb-5 text-[13px] intl-text-secondary">
                  No contracts found for this VIN.
                </div>
              ) : (
                <AssociatedContractsTable
                  contracts={contracts}
                  lastRecordedOdometer={detail?.lastRecordedOdometer}
                />
              )}
            </div>
          </div>
        </div>

        <AdditionalVehicleSheet
          open={moreDetailsOpen}
          onOpenChange={setMoreDetailsOpen}
          detail={detail}
        />
      </div>
    </TooltipProvider>
  );
}

export function VinDetailsPage() {
  const data = useLoaderData() as VinLoaderData;
  const navigate = useNavigate();

  if (data.kind === 'invalid') {
    return (
      <ErrorCard>
        <p className="text-[14px] text-red-700">{data.message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/')}
        >
          Back to search
        </Button>
      </ErrorCard>
    );
  }

  return (
    <Suspense
      key={data.vin}
      fallback={
        <div className="min-h-full bg-white p-8 text-center intl-text-secondary">
          Loading vehicle record...
        </div>
      }
    >
      <Await resolve={data.result}>
        {(loaded: VinLoaded) => <LoadedVin vin={data.vin} loaded={loaded} />}
      </Await>
    </Suspense>
  );
}
